import { ApolloError } from "@apollo/client";
import { useCallback } from "react";
import {
  useUpdateCart,
  UpdateCartData,
  UpdateCartProps,
} from "src/hooks/useUpdateCart";
import { CartInfo } from "src/types/cartInfo";
import { logger } from "src/utils/helper/logger";

interface RecalculateCartHook {
  data?: UpdateCartData;
  loading: boolean;
  error?: ApolloError;
  recalculateCart: (cartInfo: CartInfo) => Promise<void>;
}

function useRecalculateCart({
  refetchQueries = [],
  onCompleted,
  onError,
}: UpdateCartProps = {}): RecalculateCartHook {
  const { updateCart, loading, error } = useUpdateCart({
    refetchQueries,
    onCompleted: (updatedCartData: UpdateCartData) => {
      if (onCompleted) {
        onCompleted(updatedCartData);
      }
    },
    onError,
  });

  const recalculateCart: RecalculateCartHook["recalculateCart"] = useCallback(
    async (cartInfo: CartInfo) => {
      try {
        await updateCart({
          cartInfo,
          actions: [
            {
              recalculate: {
                updateProductData: true,
              },
            },
          ],
        });
      } catch (error) {
        logger({ message: "Error updating cart:", error: error as Error });
      }
    },
    [updateCart]
  );

  return { recalculateCart, loading, error };
}

export { useRecalculateCart };
